import React, { useMemo } from "react";

/**
 * TimezoneSelect — IANA timezone dropdown.
 *
 * Uses `Intl.supportedValuesOf('timeZone')` when available (all modern browsers
 * since 2022 — Chrome 99, Firefox 93, Safari 15.4). Falls back to a curated
 * shortlist when the API is missing so the picker still renders something
 * usable on older clients.
 *
 * Renders as a native <select> for accessibility + free keyboard nav. The
 * caller controls width + theme classes via `className`.
 */
const FALLBACK_TIMEZONES = [
  "UTC",
  "America/Los_Angeles", "America/Denver", "America/Chicago", "America/New_York",
  "America/Sao_Paulo", "America/Argentina/Buenos_Aires",
  "Europe/London", "Europe/Lisbon", "Europe/Madrid", "Europe/Paris",
  "Europe/Berlin", "Europe/Amsterdam", "Europe/Rome", "Europe/Stockholm",
  "Europe/Warsaw", "Europe/Athens", "Europe/Istanbul", "Europe/Moscow",
  "Asia/Dubai", "Asia/Karachi", "Asia/Kolkata", "Asia/Bangkok",
  "Asia/Singapore", "Asia/Hong_Kong", "Asia/Shanghai", "Asia/Tokyo", "Asia/Seoul",
  "Australia/Perth", "Australia/Sydney", "Pacific/Auckland",
];

function getTimezones() {
  try {
    if (typeof Intl?.supportedValuesOf === "function") {
      const zones = Intl.supportedValuesOf("timeZone");
      if (Array.isArray(zones) && zones.length) return zones;
    }
  } catch (_e) { /* fall through to fallback list */ }
  return FALLBACK_TIMEZONES;
}

export function TimezoneSelect({
  value,
  onChange,
  dataTestid = "timezone-select",
  className = "",
  ariaLabel = "Timezone",
}) {
  const zones = useMemo(getTimezones, []);
  // Ensure the current value is selectable even if it's not in the list
  // (e.g. legacy free-text input from the old picker).
  const list = useMemo(() => {
    if (value && !zones.includes(value)) return [value, ...zones];
    return zones;
  }, [zones, value]);
  return (
    <select
      value={value || "UTC"}
      onChange={(e) => onChange?.(e.target.value)}
      data-testid={dataTestid}
      aria-label={ariaLabel}
      className={
        className ||
        "bg-black/40 border border-white/15 rounded px-2 py-0.5 text-white text-[10px]"
      }
    >
      {list.map((z) => (
        <option key={z} value={z}>{z}</option>
      ))}
    </select>
  );
}

export default TimezoneSelect;
